'use client';

/**
 * CapacitorModalFilterHeader — reusable modal chrome
 *
 * Combines the centered blue-icon modal header (SmartTask / TotalConnectRTC
 * pattern) with an optional single-row filter bar:
 *
 *   [search icon] [filter controls…] [extra actions…] [+ New] [sort]
 *
 * Tapping the search icon expands a search input that renders OVER the filter
 * row (absolute overlay) instead of pushing the filters down. The sort toggle
 * is squeezed into the same row — there is no second row / second X.
 *
 * Usage:
 *   <CapacitorModalFilterHeader
 *     icon={Mail}
 *     title="Email"
 *     isDark={isDark}
 *     onClose={onClose}
 *     actions={<…buttons…>}
 *     filterBar={
 *       <ModalFilterBar
 *         isDark={isDark}
 *         search={{ value: q, onChange: setQ, placeholder: 'Search…' }}
 *         sort={{ ascending: sortAsc, onToggle: () => setSortAsc(v => !v) }}
 *         onCreateNew={canCreate ? handleNew : undefined}
 *         createLabel="New"
 *       >
 *         <MyFilterToggles />
 *       </ModalFilterBar>
 *     }
 *   />
 */

import React, { useEffect, useRef, useState } from 'react';
import { ArrowUpDown, ChevronDown, ChevronsDown, ChevronsUp, Filter, Plus, Search, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MOBILE_SAFE_AREA_TOP } from '@/components/work-items/mobileSafeArea';

// ============================================================================
// Shared action-button class (bordered square icon buttons used in the header)
// ============================================================================

export function capacitorHeaderActionClass(isDark: boolean): string {
  return cn(
    'inline-flex items-center justify-center rounded-lg border p-2 transition-colors disabled:opacity-50',
    isDark
      ? 'border-gray-700 bg-gray-900/50 text-gray-500 hover:border-gray-600 hover:text-gray-300'
      : 'border-gray-200 bg-white text-gray-400 hover:border-gray-300 hover:text-gray-600',
  );
}

// ============================================================================
// ModalFilterBar
// ============================================================================

export interface ModalFilterBarSearch {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /**
   * When true the search input is rendered inline in the row (taking a share
   * of the flex space) instead of the collapsed icon → overlay behaviour.
   */
  alwaysVisible?: boolean;
}

export type ModalFilterBarSort =
  | { ascending: boolean; onToggle: () => void; ascLabel?: string; descLabel?: string }
  | {
      options: { value: string; label: string }[];
      value: string;
      onChange: (value: string) => void;
    };

export interface ModalFilterBarFilterOption {
  value: string;
  label: string;
  /** Optional color dot shown in the dropdown list (status filters). */
  color?: string;
}

export interface ModalFilterBarFilters {
  options: ModalFilterBarFilterOption[];
  value: string;
  onChange: (value: string) => void;
  /** Label prefix for the dropdown trigger (default "Filter"). */
  dropdownLabel?: string;
}

export interface ModalFilterBarProps {
  isDark: boolean;
  /** Collapsible search — icon expands an input over the filter row. */
  search?: ModalFilterBarSearch;
  /**
   * Structured filter options — renders as segmented toggles when ≤3 options
   * (fully readable, horizontally scrollable) and collapses into a dropdown
   * when there are more. Only render what the caller's RBAC permits.
   */
  filters?: ModalFilterBarFilters;
  /** Custom filter controls rendered mid-row (alternative to `filters`). */
  children?: React.ReactNode;
  /** Extra buttons rendered after the filters, before + New / sort. */
  extraActions?: React.ReactNode;
  onCreateNew?: () => void;
  createLabel?: string;
  /** Optional data-tour-id applied to the "+ New" button. */
  createTourId?: string;
  /** Compact asc/desc sort toggle squeezed into the same row. */
  sort?: ModalFilterBarSort;
  className?: string;
}

const filterIconBtn = (isDark: boolean) =>
  cn(
    'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border shadow-sm transition-colors',
    isDark
      ? 'border-slate-700/70 bg-slate-800/80 text-slate-300 hover:bg-slate-700/80'
      : 'border-slate-200/80 bg-white/80 text-slate-500 hover:bg-slate-100/80',
  );

export function ModalFilterBar({
  isDark,
  search,
  filters,
  children,
  extraActions,
  onCreateNew,
  createLabel = 'New',
  createTourId,
  sort,
  className,
}: ModalFilterBarProps) {
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [filtersDropdownOpen, setFiltersDropdownOpen] = useState(false);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const filtersDropdownRef = useRef<HTMLDivElement>(null);
  const filtersDropdownBtnRef = useRef<HTMLButtonElement>(null);
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  const sortDropdownBtnRef = useRef<HTMLButtonElement>(null);
  const isSearchOpen = isSearchExpanded || (search ? search.value.trim().length > 0 : false);

  useEffect(() => {
    if (!filtersDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        filtersDropdownRef.current &&
        !filtersDropdownRef.current.contains(e.target as Node) &&
        filtersDropdownBtnRef.current &&
        !filtersDropdownBtnRef.current.contains(e.target as Node)
      ) {
        setFiltersDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler, true);
    return () => document.removeEventListener('mousedown', handler, true);
  }, [filtersDropdownOpen]);

  useEffect(() => {
    if (!sortDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(e.target as Node) &&
        sortDropdownBtnRef.current &&
        !sortDropdownBtnRef.current.contains(e.target as Node)
      ) {
        setSortDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler, true);
    return () => document.removeEventListener('mousedown', handler, true);
  }, [sortDropdownOpen]);

  const filtersNode = filters ? (
    filters.options.length <= 3 ? (
      // Segmented toggles — full labels, horizontally scrollable if cramped
      <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar">
        {filters.options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => filters.onChange(opt.value)}
            className={cn(
              'flex-shrink-0 whitespace-nowrap rounded-xl px-3 py-1.5 text-xs font-medium transition-colors',
              filters.value === opt.value
                ? 'bg-blue-600 text-white shadow-sm'
                : isDark
                  ? 'text-slate-300 hover:bg-slate-800/80'
                  : 'text-slate-600 hover:bg-slate-100/80',
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>
    ) : (
      // >3 filters → dropdown list (trigger fills the same flex-1 footprint
      // the pills container occupies, so the bar never resizes)
      <div className="relative flex min-w-0 flex-1 items-center">
        <button
          ref={filtersDropdownBtnRef}
          type="button"
          onClick={() => setFiltersDropdownOpen((o) => !o)}
          className={cn(
            'flex w-full items-center justify-between gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-semibold shadow-sm transition-all',
            filtersDropdownOpen
              ? 'border-gray-400 bg-gray-100 dark:border-gray-500 dark:bg-gray-700'
              : 'border-gray-200 bg-white/95 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700',
          )}
          aria-label={`${filters.dropdownLabel ?? 'Filter'} (currently ${filters.options.find((o) => o.value === filters.value)?.label ?? filters.value})`}
          aria-expanded={filtersDropdownOpen}
        >
          <span className="flex min-w-0 items-center gap-1.5">
            <Filter className="h-3.5 w-3.5 flex-shrink-0 text-gray-500 dark:text-gray-400" />
            <span className="min-w-0 truncate">
              {filters.options.find((o) => o.value === filters.value)?.label ?? filters.value}
            </span>
          </span>
          <ChevronDown
            className={cn('h-3 w-3 flex-shrink-0 transition-transform', filtersDropdownOpen && 'rotate-180')}
          />
        </button>
        {filtersDropdownOpen && (
          <div
            ref={filtersDropdownRef}
            className="absolute left-0 top-full z-[9999] mt-1 max-h-[60vh] w-full min-w-[10rem] overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-2xl dark:border-gray-600 dark:bg-gray-800"
          >
            <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
              {filters.dropdownLabel ?? 'Filter'}
            </p>
            {filters.options.map((opt) => {
              const isActive = filters.value === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    filters.onChange(opt.value);
                    setFiltersDropdownOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center gap-2.5 px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
                      : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700/50',
                  )}
                >
                  {opt.color ? (
                    <span
                      className="inline-flex h-3 w-3 flex-shrink-0 items-center justify-center rounded-full border"
                      style={{ backgroundColor: opt.color, borderColor: opt.color }}
                    />
                  ) : null}
                  <span className="truncate">{opt.label}</span>
                  {isActive && (
                    <span className="ml-auto h-2 w-2 flex-shrink-0 rounded-full bg-blue-500" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    )
  ) : null;

  return (
    <div
      className={cn(
        'relative z-20 border-b px-4 py-3 supports-[backdrop-filter]:backdrop-blur-xl',
        isDark ? 'border-slate-700/70 bg-slate-900/72' : 'border-slate-200/70 bg-white/85',
        className,
      )}
    >
      <div className="flex items-center gap-2">
        {search && !search.alwaysVisible ? (
          <button
            type="button"
            onClick={() => setIsSearchExpanded(true)}
            className={filterIconBtn(isDark)}
            title={search.placeholder ?? 'Search'}
            aria-label={search.placeholder ?? 'Search'}
          >
            <Search className="h-4 w-4" />
          </button>
        ) : null}

        {search?.alwaysVisible ? (
          <div className="relative min-w-0 flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search.value}
              onChange={(e) => search.onChange(e.target.value)}
              placeholder={search.placeholder ?? 'Search…'}
              className={cn(
                'h-10 w-full rounded-xl border pl-9 pr-8 text-sm outline-none transition-colors',
                isDark
                  ? 'border-slate-700/70 bg-slate-800/80 text-slate-100 placeholder-slate-500 focus:border-blue-500/60'
                  : 'border-slate-200/80 bg-white text-slate-800 placeholder-slate-400 focus:border-blue-400',
              )}
            />
            {search.value ? (
              <button
                type="button"
                onClick={() => search.onChange('')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                title="Clear search"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
        ) : null}

        {/* Filter controls get all remaining space so labels never clip */}
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {filtersNode}
          {children}
        </div>

        {extraActions}

        {onCreateNew ? (
          <button
            type="button"
            onClick={onCreateNew}
            className="flex flex-shrink-0 items-center gap-1 rounded-xl bg-blue-600 px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"
            title={createLabel}
            aria-label={createLabel}
            data-tour-id={createTourId}
          >
            <Plus className="h-3.5 w-3.5" />
            New
          </button>
        ) : null}

        {sort ? (
          'options' in sort ? (
            <div className="relative flex-shrink-0">
              <button
                ref={sortDropdownBtnRef}
                type="button"
                onClick={() => setSortDropdownOpen((o) => !o)}
                className={cn(
                  filterIconBtn(isDark),
                  sortDropdownOpen &&
                    'border-blue-300 bg-blue-100 text-blue-600 dark:border-blue-600 dark:bg-blue-900/40 dark:text-blue-400',
                )}
                title="Sort"
                aria-label="Sort"
                aria-expanded={sortDropdownOpen}
              >
                <ArrowUpDown className="h-4 w-4" />
              </button>
              {sortDropdownOpen && (
                <div
                  ref={sortDropdownRef}
                  className="absolute right-0 top-full z-[9999] mt-1 w-48 overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-2xl dark:border-gray-600 dark:bg-gray-800"
                >
                  <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    Sort by
                  </p>
                  {sort.options.map((opt) => {
                    const isActive = sort.value === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          sort.onChange(opt.value);
                          setSortDropdownOpen(false);
                        }}
                        className={cn(
                          'flex w-full items-center justify-between px-3 py-2 text-left text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                            : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700/50',
                        )}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={sort.onToggle}
              className={filterIconBtn(isDark)}
              title={sort.ascending ? sort.ascLabel ?? 'Earliest first' : sort.descLabel ?? 'Latest first'}
              aria-label={sort.ascending ? sort.ascLabel ?? 'Earliest first' : sort.descLabel ?? 'Latest first'}
            >
              {sort.ascending ? <ChevronsDown className="h-4 w-4" /> : <ChevronsUp className="h-4 w-4" />}
            </button>
          )
        ) : null}
      </div>

      {/* Expanded search input renders OVER the filter row */}
      {search && !search.alwaysVisible && isSearchOpen ? (
        <div
          className={cn(
            'absolute inset-0 z-10 flex items-center gap-2 px-4',
            isDark ? 'bg-slate-900' : 'bg-white',
          )}
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search.value}
              onChange={(e) => search.onChange(e.target.value)}
              placeholder={search.placeholder ?? 'Search…'}
              autoFocus
              className={cn(
                'h-10 w-full rounded-xl border pl-9 pr-3 text-sm outline-none transition-colors',
                isDark
                  ? 'border-slate-700/70 bg-slate-800/80 text-slate-100 placeholder-slate-500 focus:border-blue-500/60'
                  : 'border-slate-200/80 bg-white text-slate-800 placeholder-slate-400 focus:border-blue-400',
              )}
            />
          </div>
          <button
            type="button"
            onClick={() => {
              search.onChange('');
              setIsSearchExpanded(false);
            }}
            className={filterIconBtn(isDark)}
            title="Close search"
            aria-label="Close search"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : null}
    </div>
  );
}

// ============================================================================
// CapacitorModalFilterHeader — centered icon header + optional filter bar
// ============================================================================

export interface CapacitorModalFilterHeaderProps {
  icon?: LucideIcon;
  /** Pre-rendered icon node (avatar etc.) — takes priority over `icon`. */
  customIcon?: React.ReactNode;
  title: string;
  /** Contextual help affordance (e.g. <UserDocsLink/>) — rendered on the
   *  right edge directly below the close button, clear of the action
   *  buttons and within thumb reach. */
  titleExtra?: React.ReactNode;
  /** Overrides the icon circle's blue gradient with a solid accent color. */
  iconColor?: string;
  subtitle?: React.ReactNode;
  isDark: boolean;
  onClose: () => void;
  /** Buttons pinned top-left (e.g. refresh). */
  leadingActions?: React.ReactNode;
  /** Buttons pinned top-right, rendered before the close button. */
  actions?: React.ReactNode;
  /** When false, the built-in close X is hidden. */
  showCloseButton?: boolean;
  /** Pads the header down below the mobile safe-area inset. */
  applyTopSafeArea?: boolean;
  /** Optional filter bar rendered below the header (use ModalFilterBar). */
  filterBar?: React.ReactNode;
  /** Set false to drop the bottom border so header + content read as one surface. */
  showDivider?: boolean;
  className?: string;
}

export default function CapacitorModalFilterHeader({
  icon: Icon,
  customIcon,
  title,
  titleExtra,
  iconColor,
  subtitle,
  isDark,
  onClose,
  leadingActions,
  actions,
  showCloseButton = true,
  applyTopSafeArea = false,
  filterBar,
  showDivider = true,
  className,
}: CapacitorModalFilterHeaderProps) {
  return (
    <div className={cn('flex-shrink-0', className)}>
      <div
        className={cn(
          showDivider && 'border-b',
          isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white',
        )}
        style={applyTopSafeArea ? { paddingTop: MOBILE_SAFE_AREA_TOP } : undefined}
      >
        <div className="relative mx-auto max-w-4xl px-3 py-3">
          {leadingActions ? (
            <div className="absolute left-3 top-3 flex items-center gap-1.5">
              {leadingActions}
            </div>
          ) : null}

          <div className="absolute right-3 top-3 flex flex-col items-end gap-1.5">
            <div className="flex items-center gap-1.5">
              {actions}
              {showCloseButton ? (
                <button
                  type="button"
                  onClick={onClose}
                  className={capacitorHeaderActionClass(isDark)}
                  title="Close"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              ) : null}
            </div>
            {titleExtra}
          </div>

          <div className="pt-1 text-center">
            {customIcon ? (
              <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center sm:mb-3 sm:h-14 sm:w-14">
                {customIcon}
              </div>
            ) : Icon ? (
              <div
                className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg sm:mb-3 sm:h-14 sm:w-14"
                style={iconColor ? { backgroundImage: 'none', backgroundColor: iconColor } : undefined}
              >
                <Icon className="h-6 w-6 sm:h-7 sm:w-7" />
              </div>
            ) : null}
            <div className="flex items-center justify-center gap-1.5">
              <h1 className="text-lg font-bold text-gray-900 dark:text-white sm:text-xl">
                {title}
              </h1>
            </div>
            {subtitle ? (
              <div className="text-[10px] text-gray-500 dark:text-gray-400">{subtitle}</div>
            ) : null}
          </div>
        </div>
      </div>

      {filterBar}
    </div>
  );
}
